import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Listing } from '../types';
import BusinessCard from '../components/BusinessCard';
import Input from '../components/UI/Input';
import { Search, Filter, Briefcase, ShoppingCart, TrendingUp, UserPlus, LayoutGrid, List, Award, Coins, Heart } from 'lucide-react';

import { CATEGORIES } from '../constants';

const ListingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState<Listing[]>([]);
  const [filteredListings, setFilteredListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [categories, setCategories] = useState<any[]>([]);
  
  // Basic Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  // Default to showing all or filter by URL param
  const currentType = searchParams.get('type') || ''; 

  // Investment Specific Filters
  const [maxCapital, setMaxCapital] = useState('');
  const [equityRange, setEquityRange] = useState({ min: '', max: '' });
  const [trlRange, setTrlRange] = useState({ min: 1, max: 9 });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const data = await api.getListings();
      // Only show approved listings in public page
      const approved = data.filter(l => l.status === 'approved');
      setListings(approved);
      setFilteredListings(approved);
      setLoading(false);
    };
    
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/categories_simple.php`);
        const data = await response.json();
        if (data.success) {
          setCategories(data.categories || []);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    
    loadData();
    fetchCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm && searchTerm.length > 2) {
        api.trackEvent('search', undefined, searchTerm);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let result = listings;
    
    // Filter by Type (from URL or Sidebar)
    if (currentType) {
      result = result.filter(l => l.type === currentType);
    }

    // Apply Investment Specific Filters
    if (currentType === 'investment') {
        // 1. Max Capital (Price)
        if (maxCapital) {
            result = result.filter(l => l.price <= parseInt(maxCapital));
        }

        // 2. Equity Offered Range
        if (equityRange.min) {
            result = result.filter(l => (l.equityOffered || 0) >= parseInt(equityRange.min));
        }
        if (equityRange.max) {
            result = result.filter(l => (l.equityOffered || 0) <= parseInt(equityRange.max));
        }

        // 3. TRL Range
        result = result.filter(l => l.trl >= trlRange.min && l.trl <= trlRange.max);
    }

    if (searchTerm) {
      result = result.filter(l => l.title.includes(searchTerm) || l.description.includes(searchTerm));
    }
    
    if (category) {
      result = result.filter(l => l.category === category);
    }

    setFilteredListings(result);
  }, [searchTerm, category, listings, currentType, maxCapital, equityRange, trlRange]);

  const handleTypeChange = (type: string) => {
    if (type) {
      setSearchParams({ type });
    } else {
      setSearchParams({});
    }
    // Reset specialized filters when changing type
    setMaxCapital('');
    setEquityRange({ min: '', max: '' });
    setTrlRange({ min: 1, max: 9 });
  };

  // Helper to generate range options 1-9
  const rangeOptions = Array.from({ length: 9 }, (_, i) => i + 1);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row gap-8">
        
        {/* Sidebar Filters */}
        <aside className="w-full md:w-72 space-y-6">
           <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 sticky top-24">
              <div className="flex items-center gap-2 mb-6 text-gray-800 font-bold">
                <Filter size={20} /> فیلترها
              </div>
              
              <div className="space-y-6">
                 {/* Type Filter */}
                 <div>
                    <label className="text-sm font-bold text-gray-700 mb-3 block">نوع آگهی</label>
                    <div className="space-y-2">
                       <button 
                         onClick={() => handleTypeChange('')}
                         className={`w-full text-right text-sm px-3 py-2 rounded-lg transition-colors ${!currentType ? 'bg-teal-50 text-teal-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                       >
                         همه آگهی‌ها
                       </button>
                       <button 
                         onClick={() => handleTypeChange('sale')}
                         className={`w-full flex items-center gap-2 text-right text-sm px-3 py-2 rounded-lg transition-colors ${currentType === 'sale' ? 'bg-teal-50 text-teal-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                       >
                         <Briefcase size={16}/> فروش کسب‌وکار
                       </button>
                       <button 
                         onClick={() => handleTypeChange('demand')}
                         className={`w-full flex items-center gap-2 text-right text-sm px-3 py-2 rounded-lg transition-colors ${currentType === 'demand' ? 'bg-teal-50 text-teal-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                       >
                         <ShoppingCart size={16}/> آگهی‌های خرید
                       </button>
                       <button 
                         onClick={() => handleTypeChange('investment')}
                         className={`w-full flex items-center gap-2 text-right text-sm px-3 py-2 rounded-lg transition-colors ${currentType === 'investment' ? 'bg-teal-50 text-teal-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                       >
                         <TrendingUp size={16}/> فرصت سرمایه‌گذاری
                       </button>
                       <button 
                         onClick={() => handleTypeChange('investor')}
                         className={`w-full flex items-center gap-2 text-right text-sm px-3 py-2 rounded-lg transition-colors ${currentType === 'investor' ? 'bg-teal-50 text-teal-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                       >
                         <UserPlus size={16}/> پروفایل سرمایه‌گذاران
                       </button>
                       <button 
                         onClick={() => handleTypeChange('intangible')}
                         className={`w-full flex items-center gap-2 text-right text-sm px-3 py-2 rounded-lg transition-colors ${currentType === 'intangible' ? 'bg-teal-50 text-teal-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                       >
                         <Award size={16}/> آگهی‌های نامشهود
                       </button>
                       <button 
                         onClick={() => handleTypeChange('micro_investment')}
                         className={`w-full flex items-center gap-2 text-right text-sm px-3 py-2 rounded-lg transition-colors ${currentType === 'micro_investment' ? 'bg-teal-50 text-teal-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                       >
                         <Coins size={16}/> سرمایه‌گذاری خرد
                       </button>
                       <button 
                         onClick={() => handleTypeChange('philanthropic')}
                         className={`w-full flex items-center gap-2 text-right text-sm px-3 py-2 rounded-lg transition-colors ${currentType === 'philanthropic' ? 'bg-teal-50 text-teal-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                       >
                         <Heart size={16}/> کارهای عام‌المنفعه
                       </button>
                    </div>
                 </div>

                 {/* CATEGORY FILTER */}
                 <div className="border-t pt-4">
                   <label className="text-sm font-medium text-gray-600 mb-2 block">دسته‌بندی صنعت</label>
                   <select 
                      className="w-full rounded-lg border-gray-300 text-sm p-2 bg-gray-50 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                   >
                     <option value="">همه صنایع</option>
                     {categories.map(cat => (
                         <option key={cat.id} value={cat.id}>{cat.name}</option>
                     ))}
                   </select>
                 </div>
                 
                 {/* --- INVESTMENT SPECIFIC FILTERS --- */}
                 {currentType === 'investment' && (
                    <div className="border-t pt-4 space-y-5 animate-fadeIn">
                        
                        {/* 1. Max Capital */}
                        <div>
                            <label className="text-xs font-bold text-gray-700 mb-2 block">سقف سرمایه مورد نیاز (تومان)</label>
                            <input 
                                type="number" 
                                placeholder="مثلا: ۵,۰۰۰,۰۰۰,۰۰۰"
                                value={maxCapital}
                                onChange={(e) => setMaxCapital(e.target.value)}
                                className="w-full rounded-lg border-gray-300 text-sm p-2 bg-gray-50 focus:ring-teal-500"
                            />
                        </div>

                        {/* 2. Equity Range */}
                        <div>
                            <label className="text-xs font-bold text-gray-700 mb-2 block">درصد سهام پیشنهادی</label>
                            <div className="flex gap-2 items-center">
                                <input 
                                    type="number" placeholder="حداقل ٪"
                                    value={equityRange.min}
                                    onChange={(e) => setEquityRange({...equityRange, min: e.target.value})}
                                    className="w-full rounded-lg border-gray-300 text-sm p-2 bg-gray-50 text-center"
                                />
                                <span className="text-gray-400">-</span>
                                <input 
                                    type="number" placeholder="حداکثر ٪"
                                    value={equityRange.max}
                                    onChange={(e) => setEquityRange({...equityRange, max: e.target.value})}
                                    className="w-full rounded-lg border-gray-300 text-sm p-2 bg-gray-50 text-center"
                                />
                            </div>
                        </div>

                        {/* 3. TRL Range */}
                        <div>
                             <div className="flex justify-between mb-1">
                                <label className="text-xs font-bold text-gray-700">سطح فناوری (TRL)</label>
                                <span className="text-[10px] text-teal-600 font-mono">{trlRange.min} تا {trlRange.max}</span>
                             </div>
                             <div className="flex gap-2">
                                <select 
                                    value={trlRange.min}
                                    onChange={(e) => setTrlRange({...trlRange, min: parseInt(e.target.value)})}
                                    className="w-full p-1 text-xs rounded border border-gray-300 bg-gray-50"
                                >
                                    {rangeOptions.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                                <span className="text-gray-400 pt-1">تا</span>
                                <select 
                                    value={trlRange.max}
                                    onChange={(e) => setTrlRange({...trlRange, max: parseInt(e.target.value)})}
                                    className="w-full p-1 text-xs rounded border border-gray-300 bg-gray-50"
                                >
                                    {rangeOptions.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                             </div>
                        </div>
                    </div>
                 )}

                 {/* General Price Range (Only show if NOT investment, to avoid confusion with Capital Needed) */}
                 {currentType !== 'investment' && (
                     <div>
                       <label className="text-sm font-medium text-gray-600 mb-2 block">
                           {currentType === 'investor' ? 'سقف سرمایه‌گذاری' : 'محدوده قیمت (تومان)'}
                       </label>
                       <input type="range" className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600" />
                       <div className="flex justify-between text-xs text-gray-500 mt-1">
                         <span>۰</span>
                         <span>۵۰ م+</span>
                       </div>
                     </div>
                 )}
              </div>
           </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1">
           {/* Search Bar */}
           <div className="mb-6 relative">
              <Search className="absolute right-3 top-3 text-gray-400" size={20} />
              <input 
                type="text"
                placeholder="جستجو در عنوان و توضیحات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-10 py-3 rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-teal-500 outline-none"
              />
           </div>

           {/* Results Header */}
           <div className="mb-4 text-gray-500 text-sm flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-start">
                  <span>
                      نمایش {filteredListings.length} مورد
                      {currentType && <span className="mr-1 font-bold text-teal-600">
                        (
                          {currentType === 'sale' && 'فروش کسب‌وکار'}
                          {currentType === 'demand' && 'تقاضای خرید'}
                          {currentType === 'investment' && 'فرصت سرمایه‌گذاری'}
                          {currentType === 'investor' && 'پروفایل سرمایه‌گذار'}
                          {currentType === 'intangible' && 'آگهی نامشهود'}
                          {currentType === 'micro_investment' && 'سرمایه‌گذاری خرد'}
                          {currentType === 'philanthropic' && 'عام‌المنفعه'}
                        )
                      </span>}
                  </span>
                  
                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1">
                      <button 
                        onClick={() => setViewMode('grid')}
                        className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-teal-50 text-teal-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                        title="نمایش شبکه‌ای"
                      >
                          <LayoutGrid size={18}/>
                      </button>
                      <button 
                        onClick={() => setViewMode('list')}
                        className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-teal-50 text-teal-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                        title="نمایش لیستی"
                      >
                          <List size={18}/>
                      </button>
                  </div>
              </div>

              {(maxCapital || equityRange.min || equityRange.max || trlRange.min > 1) && (
                  <button 
                    onClick={() => {
                        setMaxCapital('');
                        setEquityRange({ min: '', max: '' });
                        setTrlRange({ min: 1, max: 9 });
                    }}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                      حذف فیلترهای پیشرفته
                  </button>
              )}
           </div>

           {/* Results Grid */}
           {loading ? (
             <div className="text-center py-20">در حال بارگذاری...</div>
           ) : filteredListings.length === 0 ? (
             <div className="text-center py-20 bg-white rounded-xl border border-gray-100">
                <p className="text-gray-500">موردی با این مشخصات یافت نشد.</p>
                <button onClick={() => {setCategory(''); setSearchTerm(''); handleTypeChange('')}} className="mt-4 text-teal-600 text-sm hover:underline">
                  پاک کردن تمام فیلترها
                </button>
             </div>
           ) : (
             <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "flex flex-col gap-4"}>
               {filteredListings.map(item => (
                 <BusinessCard key={item.id} listing={item} layout={viewMode} />
               ))}
             </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default ListingsPage;